import { capitalize, Chip, CurrencyMinor, Icon, IconName, Typography } from '@fox/atoms';
import { PaymentMethod, ProviderCapability } from '@fox/finance';
import { SpaceBetweenRow } from '@fox/layout';
import { ChipProps } from '@mui/material';
import * as moment from 'moment';
import { ComponentProps, ReactNode } from 'react';
import {
	FeesSelection,
	FriendlyInvoiceStatus,
	Interval,
	InvoiceStatus,
	PlanOption,
	Receipt,
	SubscriptionStatus,
	SwiftFoxPlansResult,
	SwiftFoxSubscriptionDetailed
} from './contracts';

export const DEFAULT_CURRENCY_SYMBOL = '$';
export const intervalDisplayShort = (interval: Interval, units?: number) => {
	return `${units > 1 ? `${units} ` : ''}${INTERVAL_SHORT[interval]}${units > 1 ? 's' : ''}`;
};
export function FeeSummary({
	selectedFee,
	nominatedFee
}: {
	selectedFee?: Omit<SwiftFoxPlansResult['fees'][number], 'id' | 'planId'>;
	nominatedFee?: number;
}) {
	if (!selectedFee) return null;

	const renewal = selectedFee.renewalFee ?? 0;
	const currency = selectedFee?.currency ?? 'AUD';
	const fee = selectedFee.fee;

	const feeAmount = (nominatedFee ?? 0) + (renewal > 0 && selectedFee.interval === Interval.ANNUAL ? renewal + fee : fee);

	return (
		<>
			{renewal && selectedFee.interval !== Interval.ANNUAL ? <>{formatCurrency(renewal, currency)} +&nbsp;</> : ''}
			{formatCurrency(feeAmount, currency)}
			{'/'}
			{intervalDisplayShort(selectedFee.interval, selectedFee.units)}

			{selectedFee.interval !== Interval.ANNUAL ? '' : selectedFee.interval !== Interval.ANNUAL}
		</>
	);
}

export const currencySymbolDict = {
	CHF: '₣', // Swiss Franc
	EUR: '€', // Euro
	CRC: '₡', // Costa Rican Colón
	GBP: '£', // British Pound Sterling
	ILS: '₪', // Israeli New Sheqel
	INR: '₹', // Indian Rupee
	JPY: '¥', // Japanese Yen
	CNY: '¥', // Chinese Yuan
	KRW: '₩', // South Korean Won
	NGN: '₦', // Nigerian Naira
	PHP: '₱', // Philippine Peso
	PLN: 'zł', // Polish Zloty
	PYG: '₲', // Paraguayan Guarani
	THB: '฿', // Thai Baht
	UAH: '₴', // Ukrainian Hryvnia
	VND: '₫' // Vietnamese Dong
};
export function baseCurrency(baseAmount: number | undefined) {
	const value = (baseAmount ?? 0) / 100;
	return value.toFixed(Number.isInteger(value) ? 0 : 2);
}
export const dict: Record<SubscriptionStatus, { color: ComponentProps<typeof Chip>['color']; icon?: IconName }> = {
	[SubscriptionStatus.Active]: { color: 'success' },
	[SubscriptionStatus.Ended]: { color: 'warning' },
	[SubscriptionStatus.Cancelled]: { color: 'default' },
	[SubscriptionStatus.Pending]: { color: 'info' },
	[SubscriptionStatus.OnHold]: { color: 'warning', icon: 'Pause' }
};
export const renderStatus = (value: SubscriptionStatus) => FRIENDLY_STATUSES[value] ?? UNKNOWN_LABEL;

export const UNKNOWN_LABEL = 'Unknown';

export const INTERVAL_SHORT: Record<Interval, string> = {
	[Interval.ANNUAL]: 'year',
	[Interval.DAILY]: 'day',
	[Interval.FORTNIGHTLY]: 'fortnight',
	[Interval.FOUR_WEEKLY]: 'four week',
	[Interval.MONTHLY]: 'month',
	[Interval.QUARTERLY]: 'quarter',
	[Interval.WEEKLY]: 'week'
};

export const FRIENDLY_INTERVAL: Record<Interval, string> = {
	[Interval.ANNUAL]: 'Annual',
	[Interval.DAILY]: 'Daily',
	[Interval.FORTNIGHTLY]: 'Fortnightly',
	[Interval.FOUR_WEEKLY]: 'Four weekly',
	[Interval.MONTHLY]: 'Monthly',
	[Interval.QUARTERLY]: 'Quarterly',
	[Interval.WEEKLY]: 'Weekly'
};

export const FRIENDLY_STATUSES: Record<SubscriptionStatus, string> = {
	[SubscriptionStatus.Active]: 'Active',
	[SubscriptionStatus.OnHold]: 'On hold',
	[SubscriptionStatus.Cancelled]: 'Cancelled',
	[SubscriptionStatus.Ended]: 'Ended',
	[SubscriptionStatus.Pending]: 'Pending'
};

export function SubscriptionStatusChip({ status }: { status: SubscriptionStatus }) {
	const { color, icon } = dict[status];
	return <Chip icon={icon ? <Icon iconName={icon} /> : null} label={renderStatus(status)} color={color} />;
}

export function SubscriptionAutoRenewStatusChip({ autoRenew }: { autoRenew: boolean }) {
	return <Chip label={autoRenew ? 'Active' : 'Inactive'} color={autoRenew ? 'success' : 'default'} />;
}

/** takes the baseAmount (cents or equivalent atomic unit)
 * and formats it into a more readable string based on the currency
 * */
export function formatCurrency(baseAmount?: number, currency: string = 'AUD') {
	return `${currencySymbolDict[currency] ?? DEFAULT_CURRENCY_SYMBOL}${baseCurrency(baseAmount)}`;
}

/** used in moment.format() */
export const dateTimeFormat = 'DD/MM/YYYY hh:mm A';

export const dateFormat = 'DD/MM/YYYY';

/** calculates the total of the lineItems in a receipt  */
export function getReceiptTotal(receipt: Receipt): CurrencyMinor {
	return (receipt?.lineItems?.reduce((total, item) => total + item.amount, 0) as CurrencyMinor) ?? (0 as CurrencyMinor);
}

export function ReceiptStatusChip({ receipt: r }: { receipt: Pick<Receipt, 'success' | 'refunded' | 'pending' | 'status'> }) {
	return (
		<Chip
			color={
				r.status === 'Replaced'
					? 'secondary'
					: !r.success
					? 'error'
					: r.refunded
					? 'secondary'
					: r.pending
					? 'warning'
					: r.success
					? 'primary'
					: 'error'
			}
			label={
				r.status === 'Replaced'
					? 'Resolved'
					: !r.success
					? 'Failed'
					: r.refunded
					? 'Refunded'
					: r.pending
					? 'Pending'
					: r.success
					? 'Success'
					: 'CASE NOT HANDLED'
			}
		/>
	);
}

export function ReceiptStatusIcon({ receipt: r }: { receipt: Pick<Receipt, 'success' | 'refunded' | 'pending' | 'status'> }) {
	const color =
		r.status === 'Replaced'
			? 'secondary'
			: !r.success
			? 'error'
			: r.refunded
			? 'secondary'
			: r.pending
			? 'warning'
			: r.success
			? 'primary'
			: 'error';
	const iconName = !r.success
		? 'ErrorOutline'
		: r.refunded
		? 'PriceCheck'
		: r.pending
		? 'Pending'
		: r.success
		? 'CheckCircle'
		: 'Announcement';

	return <Icon iconName={iconName as IconName} color={color} />;
}

export function renderDate(value) {
	if (!value) return '';
	return moment(value).format('DD/MM/YYYY');
}

//enums are annoying and they do not line up between sf and umbraco backend
export function convertMemberPortalToSfMethodEnumValue(input) {
	if (typeof input === 'string' && PaymentMethod[input]) return PaymentMethod[input];

	switch (input) {
		case MemberPortalPaymentMethod.CASH:
			return PaymentMethod.Cash;
		case MemberPortalPaymentMethod.CHEQUE:
			return PaymentMethod.Cheque;
		case MemberPortalPaymentMethod.CREDIT_CARD:
			return PaymentMethod.CreditCard;
		case MemberPortalPaymentMethod.DIRECT_DEBIT:
			return PaymentMethod.DirectDebit;
		case MemberPortalPaymentMethod.GENERIC:
			return PaymentMethod.Generic;
		case MemberPortalPaymentMethod.PAYROLL_DEDUCTION:
			return PaymentMethod.PayrollDeduction;
		case MemberPortalPaymentMethod.STATEMENT:
			return PaymentMethod.Statement;
		default:
			return;
	}
}

export function convertSfToMemberPortalMethodEnumValue(input) {
	switch (input) {
		case PaymentMethod.Cash:
			return MemberPortalPaymentMethod.CASH;
		case PaymentMethod.Cheque:
			return MemberPortalPaymentMethod.CHEQUE;
		case PaymentMethod.CreditCard:
			return MemberPortalPaymentMethod.CREDIT_CARD;
		case PaymentMethod.DirectDebit:
			return MemberPortalPaymentMethod.DIRECT_DEBIT;
		case PaymentMethod.Generic:
			return MemberPortalPaymentMethod.GENERIC;
		case PaymentMethod.PayrollDeduction:
			return MemberPortalPaymentMethod.PAYROLL_DEDUCTION;
		case PaymentMethod.Statement:
			return MemberPortalPaymentMethod.STATEMENT;
		default:
			return;
	}
}

export enum MemberPortalProviderCapability {
	ADD_METHOD,
	IMMEDIATE_PAYMENT,
	IMMEDIATE_REFUND,
	OUT_OF_BAND,
	SCHEDULES
}

export function convertMemberPortalCapabilityToSfEnumValue(input) {
	switch (input) {
		case MemberPortalProviderCapability.ADD_METHOD:
			return ProviderCapability.AddMethod;
		case MemberPortalProviderCapability.IMMEDIATE_PAYMENT:
			return ProviderCapability.ImmediatePayment;
		case MemberPortalProviderCapability.IMMEDIATE_REFUND:
			return ProviderCapability.ImmediatePayment;
		case MemberPortalProviderCapability.OUT_OF_BAND:
			return ProviderCapability.OutOfBand;
		case MemberPortalProviderCapability.SCHEDULES:
			return ProviderCapability.Schedules;
	}
}

export enum MemberPortalPaymentMethod {
	CASH,
	CHEQUE,
	CREDIT_CARD,
	DIRECT_DEBIT,
	GENERIC,
	PAYROLL_DEDUCTION,
	STATEMENT
}

export function findSimilarPlanOptions(input: {
	plan: SwiftFoxPlansResult;
	fee: FeesSelection;
	subscription: SwiftFoxSubscriptionDetailed;
}) {
	const { fee, plan, subscription } = input;
	if (!fee) return [];
	return plan.options?.reduce((prev, cur) => {
		if (
			(cur.interval === null || (cur.interval === fee.interval && cur.units === fee.units)) &&
			subscription.options.some(opt => opt.name === cur.name) &&
			!prev.find(o => o.name === cur.name && o.interval === cur.interval && o.units === cur.units)
		) {
			prev.push(cur);
		}
		return prev;
	}, [] as PlanOption[]);
}

const INVOICE_STATUS_COLOR_DICT: Record<InvoiceStatus, ChipProps['color']> = {
	[InvoiceStatus.VOIDED]: 'default',
	[InvoiceStatus.DELETED]: 'default',
	[InvoiceStatus.DRAFT]: 'info',
	[InvoiceStatus.PART_PAID]: 'info',
	[InvoiceStatus.PAID]: 'success',
	[InvoiceStatus.AUTHORISED]: 'success',
	[InvoiceStatus.SUBMITTED]: 'warning'
} as const;

export function InvoiceStatusChip({ status }: { status: InvoiceStatus }) {
	return status !== undefined && status !== null ? (
		<Chip color={INVOICE_STATUS_COLOR_DICT[status] ?? 'default'} label={capitalize(FriendlyInvoiceStatus[status])} />
	) : null;
}

export function InvoiceStatusIcon({ status }: { status: InvoiceStatus }) {
	const color = INVOICE_STATUS_COLOR_DICT[status];
	const colorToUse = color === 'default' ? 'error' : color;
	const iconName =
		colorToUse === 'success' ? 'CheckCircle' : colorToUse === 'info' ? 'Info' : colorToUse === 'warning' ? 'Warning' : 'ErrorOutline';

	return <Icon iconName={iconName as IconName} color={colorToUse} />;
}

export function DrawerLine({ title, children }: { title: ReactNode; children: ReactNode }) {
	return (
		<SpaceBetweenRow>
			<Typography style={{ fontWeight: 'bold' }}>{title}</Typography>
			<Typography>{children}</Typography>
		</SpaceBetweenRow>
	);
}

export const EMPTY_FIELD_PLACEHOLDER = 'N/A';

export const TABLE_BUTTON_STYLE = { padding: '0.25rem 0.5rem', fontSize: '1rem' } as const;
